/**
 * Blood Bowl Calculator - Internationalization
 *
 * Translations for English and French
 */

export type Language = "en" | "fr";

export interface Translations {
  // Header
  title: string;
  subtitle: string;

  // Team Rerolls
  teamRerolls: string;
  usedOf: string;

  // Players Section
  players: string;
  addPlayer: string;
  player: string;
  removePlayer: string;

  // Skills
  skills: {
    dodge: string;
    sure_hands: string;
    pass: string;
    catch: string;
    sure_feet: string;
    block: string;
  };

  // Loner
  loner: string;

  // Actions Section
  actionSequence: string;
  addActionGroup: string;
  addAction: string;
  removeActionGroup: string;
  removeAction: string;
  noActions: string;

  // Action Labels
  action: string;
  result: string;
  reroll: string;

  // Action Types
  actionTypes: {
    dodge: string;
    gfi: string;
    pickup: string;
    pass: string;
    catch: string;
    throw_teammate: string;
    landing: string;
    block_3d: string;
    block_2d: string;
    block_1d: string;
    "block_-2d": string;
    "block_-3d": string;
    armor: string;
    injury: string;
    roll: string;
  };

  // Block Results
  blockResults: {
    pushed_or_pow: string;
    pow_only: string;
    pow_no_dodge: string;
    pow_or_both_down: string;
    no_skull: string;
  };

  // Injury Results
  injuryResults: {
    stunned_plus: string;
    ko_plus: string;
    casualty: string;
  };

  // Reroll Options
  rerollOptions: {
    auto: string;
    none: string;
    team: string;
  };

  // Reroll Types
  rerollTypes: {
    skill: string;
    "skill-shared": string;
    team: string;
    none: string;
  };

  // Calculate Button
  calculateButton: string;

  // Results Section
  results: string;
  totalProbability: string;
  teamRerollsUsed: string;
  detailByAction: string;
  formula: string;

  // Risk Levels
  riskLevels: {
    safe: string;
    reasonable: string;
    risky: string;
    very_risky: string;
    desperate: string;
  };

  // Player Selection
  noPlayer: string;

  // Alerts
  addActionAlert: string;

  // Comparison Mode
  comparison: {
    title: string;
    enterMode: string;
    exitMode: string;
    editing: string;
    editingChoice: string;
    winner: string;
    noActions: string;
    calculating: string;
    actions: string;
    tip: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    title: "Blood Bowl",
    subtitle: "Probability Calculator",

    teamRerolls: "Available Team Rerolls",
    usedOf: "used of",

    players: "Players",
    addPlayer: "+ Player",
    player: "Player #",
    removePlayer: "Remove player",

    skills: {
      dodge: "Dodge",
      sure_hands: "Sure Hands",
      pass: "Pass",
      catch: "Catch",
      sure_feet: "Sure Feet",
      block: "Block",
    },

    loner: "Loner",

    actionSequence: "Action Sequence",
    addActionGroup: "+ Action Group",
    addAction: "+ Action",
    removeActionGroup: "Remove action group",
    removeAction: "Remove action",
    noActions: "No actions. Click \"+ Action\" to add one.",

    action: "Action",
    result: "Result",
    reroll: "Reroll",

    actionTypes: {
      dodge: "Dodge",
      gfi: "GFI",
      pickup: "Pickup",
      pass: "Pass",
      catch: "Catch",
      throw_teammate: "Throw Teammate",
      landing: "Landing",
      block_3d: "Block 3D",
      block_2d: "Block 2D",
      block_1d: "Block 1D",
      "block_-2d": "Block -2D",
      "block_-3d": "Block -3D",
      armor: "Armor",
      injury: "Injury",
      roll: "Simple Roll",
    },

    blockResults: {
      pushed_or_pow: "Push or Pow",
      pow_only: "Pow Only",
      pow_no_dodge: "Pow (no Dodge)",
      pow_or_both_down: "Pow or Both Down",
      no_skull: "No Skull",
    },

    injuryResults: {
      stunned_plus: "Stunned+",
      ko_plus: "KO+",
      casualty: "Casualty",
    },

    rerollOptions: {
      auto: "Auto",
      none: "None",
      team: "Team",
    },

    rerollTypes: {
      skill: "Skill",
      "skill-shared": "Skill (shared)",
      team: "Team",
      none: "None",
    },

    calculateButton: "Calculate Probabilities",

    results: "Results",
    totalProbability: "Total Success Probability",
    teamRerollsUsed: "Team rerolls used",
    detailByAction: "Detail by Action",
    formula: "Formula",

    riskLevels: {
      safe: "Safe",
      reasonable: "Reasonable",
      risky: "Risky",
      very_risky: "Very Risky",
      desperate: "Desperate",
    },

    noPlayer: "-- None --",

    addActionAlert: "Add at least one action!",

    comparison: {
      title: "Compare Choices",
      enterMode: "Compare",
      exitMode: "Exit Compare",
      editing: "Editing",
      editingChoice: "Editing choice",
      winner: "Best",
      noActions: "Click to add actions",
      calculating: "Calculating...",
      actions: "actions",
      tip: "Click a choice to edit it. The better option is highlighted.",
    },
  },

  fr: {
    title: "Blood Bowl",
    subtitle: "Calculateur de Probabilités",

    teamRerolls: "Relances d'équipe disponibles",
    usedOf: "utilisée sur",

    players: "Joueurs",
    addPlayer: "+ Joueur",
    player: "Joueur #",
    removePlayer: "Supprimer joueur",

    skills: {
      dodge: "Esquive",
      sure_hands: "Prise Sûre",
      pass: "Passe",
      catch: "Réception",
      sure_feet: "Pieds Agiles",
      block: "Block",
    },

    loner: "Solitaire",

    actionSequence: "Séquence d'Actions",
    addActionGroup: "+ Groupe d'Actions",
    addAction: "+ Action",
    removeActionGroup: "Supprimer groupe d'actions",
    removeAction: "Supprimer action",
    noActions: "Aucune action. Cliquez sur \"+ Action\" pour en ajouter.",

    action: "Action",
    result: "Résultat",
    reroll: "Relance",

    actionTypes: {
      dodge: "Esquive",
      gfi: "GFI",
      pickup: "Ramassage",
      pass: "Passe",
      catch: "Réception",
      throw_teammate: "Lancer coéquipier",
      landing: "Atterrissage",
      block_3d: "Block 3D",
      block_2d: "Block 2D",
      block_1d: "Block 1D",
      "block_-2d": "Block -2D",
      "block_-3d": "Block -3D",
      armor: "Armure",
      injury: "Blessure",
      roll: "Jet simple",
    },

    blockResults: {
      pushed_or_pow: "Repoussé ou Pow",
      pow_only: "Pow uniquement",
      pow_no_dodge: "Pow sans Esquive",
      pow_or_both_down: "Pow ou Both Down",
      no_skull: "Pas de Skull",
    },

    injuryResults: {
      stunned_plus: "Sonné+",
      ko_plus: "KO+",
      casualty: "Sortie",
    },

    rerollOptions: {
      auto: "Auto",
      none: "Aucune",
      team: "Équipe",
    },

    rerollTypes: {
      skill: "Compétence",
      "skill-shared": "Comp. (partagée)",
      team: "Équipe",
      none: "Aucune",
    },

    calculateButton: "Calculer les Probabilités",

    results: "Résultats",
    totalProbability: "Probabilité de Succès Total",
    teamRerollsUsed: "Relances d'équipe utilisées",
    detailByAction: "Détail par Action",
    formula: "Formule",

    riskLevels: {
      safe: "Sûr",
      reasonable: "Raisonnable",
      risky: "Risqué",
      very_risky: "Très Risqué",
      desperate: "Désespéré",
    },

    noPlayer: "-- Aucun --",

    addActionAlert: "Ajoutez au moins une action!",

    comparison: {
      title: "Comparer les Choix",
      enterMode: "Comparer",
      exitMode: "Quitter",
      editing: "En cours",
      editingChoice: "Choix en cours",
      winner: "Meilleur",
      noActions: "Cliquez pour ajouter des actions",
      calculating: "Calcul en cours...",
      actions: "actions",
      tip: "Cliquez sur un choix pour le modifier. La meilleure option est mise en avant.",
    },
  },
};
